import { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleAlert, FileDown, History, Info, Printer, ShieldAlert, ShieldCheck, TriangleAlert } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import { CustomizableMetrics, MetricTile, StatusPill, Tabs, type TabItem, WorkspacePage } from '@forq/ui';
import { buildBookingRows, exportErrors, mayExport, reconcileExport, validateForExport } from '@forq/finance-data';
import { accountLabel } from '@forq/finance-data';
import { datevSettingsIssues, isDatevConfigured } from '@forq/finance-data';
import {
  checkNumberRanges,
  checksumOf,
  classifyRepeat,
  exportFormatLabels,
  formatGap,
  hasNumberGaps,
  type ExportFormat,
  type ExportLogEntry,
  type ExportLogGateway,
} from '@forq/finance-data';
import { isSellerConfigured, sellerSettingsIssues } from '@forq/finance-data';
import { emptyCompanyProfile, type CompanyProfileGateway, type TenantCompanyProfile } from '@forq/platform-data';
// Verkaeufer- und DATEV-Angaben kommen aus der Grundkonfiguration -- eine Quelle je Mandant.
import { datevSettingsFromProfile, grundkonfigurationHinweis, sellerSettingsFromProfile } from './companySettings';
import { buildExtf, downloadExtf, extfFileName } from '@forq/finance-data';
import { taxCodeDefinition } from '@forq/finance-data';
import { downloadCsv, downloadDatevCsv, toDatevCsv, toVatCsv } from '@forq/finance-data';
import { FinanceNav } from './FinanceNav';
import { computeVatReport, incomingInPeriod, invoicesInPeriod, periodLabel } from '@forq/finance-data';
import { dateLabel, errorMessage, money } from '@forq/finance-data';
import { PeriodFilter, useFinancePeriod } from './PeriodFilter';
import { todayIso, type FinanceGateway, type Invoice } from '@forq/finance-data';
import type { IncomingInvoice, IncomingInvoiceGateway } from '@forq/finance-data';

interface BuchhaltungPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly userName: string;
  readonly financeGateway: FinanceGateway;
  readonly incomingGateway: IncomingInvoiceGateway;
  readonly exportLogGateway: ExportLogGateway;
  readonly companyGateway: CompanyProfileGateway;
}

type BookkeepingView = 'vat' | 'stack' | 'log';

const viewTabs: readonly TabItem[] = [
  { id: 'vat', label: 'Umsatzsteuer / Vorsteuer' },
  { id: 'stack', label: 'Buchungsstapel' },
  { id: 'log', label: 'Übergaben & Nummernkreis' },
];

/**
 * Vorbereitende Buchhaltung. Deliberately NOT a Fibu: FORQ derives the USt/Vorsteuer figures from
 * its own booked documents so they can be CHECKED and handed over — the legal filing stays in the
 * Fibu/ELSTER. The legacy FORQ draws exactly this line and labels its own report "vorbereitend".
 */
export function BuchhaltungPage({
  tenant, mode, permissions, userName, financeGateway, incomingGateway, exportLogGateway, companyGateway,
}: BuchhaltungPageProps) {
  const [period, setPeriod] = useFinancePeriod();
  const [view, setView] = useState<BookkeepingView>('vat');
  const [invoices, setInvoices] = useState<readonly Invoice[]>([]);
  const [incoming, setIncoming] = useState<readonly IncomingInvoice[]>([]);
  const [exports, setExports] = useState<readonly ExportLogEntry[]>([]);
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const settings = useMemo(() => datevSettingsFromProfile(company), [company]);
  const seller = useMemo(() => sellerSettingsFromProfile(company), [company]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const today = todayIso();
  const canExport = permissions.has('finance.view');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invoiceList, incomingList, exportList] = await Promise.all([
        financeGateway.listInvoices({ tenantId: tenant.id }),
        incomingGateway.listIncomingInvoices({ tenantId: tenant.id }),
        exportLogGateway.listExports(tenant.id),
      ]);
      setInvoices(invoiceList);
      setIncoming(incomingList);
      setExports(exportList);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [financeGateway, incomingGateway, exportLogGateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    void companyGateway.loadProfile(tenant.id).then(setCompany).catch(() => setCompany(emptyCompanyProfile));
  }, [companyGateway, tenant.id]);

  const vat = useMemo(() => computeVatReport(invoices, incoming, period), [invoices, incoming, period]);
  // The stack follows the chosen period — an export must never contain more than the screen shows.
  const periodInvoices = useMemo(() => invoicesInPeriod(invoices, period), [invoices, period]);
  const periodIncoming = useMemo(() => incomingInPeriod(incoming, period), [incoming, period]);
  const stackRows = useMemo(() => buildBookingRows(periodInvoices, periodIncoming), [periodInvoices, periodIncoming]);
  const issues = useMemo(() => validateForExport(periodInvoices, periodIncoming), [periodInvoices, periodIncoming]);
  const errors = useMemo(() => exportErrors(issues), [issues]);
  const reconciliation = useMemo(
    () => reconcileExport(stackRows, periodInvoices, periodIncoming),
    [stackRows, periodInvoices, periodIncoming],
  );
  /** A bad stapel is worse than none: the tax advisor books straight from it. */
  const blocked = !mayExport(issues) || !reconciliation.ok;

  const numberRanges = useMemo(() => checkNumberRanges(invoices), [invoices]);
  const gapsFound = hasNumberGaps(numberRanges);
  const csvContent = useMemo(() => toDatevCsv(stackRows), [stackRows]);
  const checksum = useMemo(() => checksumOf(csvContent), [csvContent]);
  /** Was this period handed over before, and has it changed since? */
  const repeat = useMemo(() => classifyRepeat(exports, period, checksum), [exports, period, checksum]);
  const datevReady = isDatevConfigured(settings);

  /**
   * The checksum fingerprints the BOOKINGS, not the file bytes — always derived from the canonical
   * CSV rendering, whatever format was downloaded. Hashing the file instead made an EXTF export
   * look "changed" against its own CSV twin and raised a false alarm; a warning that cries wolf is
   * worse than none, because the real double-booking then gets waved through too.
   */
  const logExport = useCallback(async (format: ExportFormat, fileName: string) => {
    await exportLogGateway.recordExport({
      tenantId: tenant.id,
      period,
      exportedBy: userName,
      format,
      rowCount: stackRows.length,
      debitorTotal: reconciliation.debitorTotal,
      kreditorTotal: reconciliation.kreditorTotal,
      checksum,
      fileName,
    });
    setExports(await exportLogGateway.listExports(tenant.id));
  }, [exportLogGateway, tenant.id, period, userName, stackRows.length, reconciliation, checksum]);

  const exportDatev = useCallback(async () => {
    if (blocked) return;
    downloadDatevCsv(csvContent, tenant.shortName);
    await logExport('csv', `FORQ_Buchungsstapel_${tenant.shortName}_DATEV.csv`);
  }, [blocked, csvContent, tenant.shortName, logExport]);

  const exportExtf = useCallback(async () => {
    if (blocked || !datevReady) return;
    const fileName = extfFileName(tenant.shortName, period);
    const content = buildExtf(stackRows, {
      consultantNumber: settings.consultantNumber,
      clientNumber: settings.clientNumber,
      fiscalYearStart: settings.fiscalYearStart,
      accountLength: settings.accountLength,
      period,
      description: `FORQ ${periodLabel(period)}`,
      createdAt: new Date().toISOString(),
      createdBy: userName,
    });
    downloadExtf(content, fileName);
    await logExport('extf', fileName);
  }, [blocked, datevReady, tenant.shortName, period, stackRows, settings, userName, logExport]);

  const sellerReady = isSellerConfigured(seller);

  const refund = vat.payable < 0;

  return (
    <WorkspacePage
      customizeKey="finance-buchhaltung"
      eyebrow={`FORQ Finance · Buchhaltung · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Buchhaltung & Steuern"
      description="Vorbereitende Buchhaltung: Umsatzsteuer und Vorsteuer prüfen, Buchungsstapel an die Fibu übergeben."
      actions={(
        <>
          <button type="button" className="finance-actions__btn" onClick={() => window.print()}>
            <Printer size={15} /> Drucken
          </button>
          <button
            type="button"
            className="finance-actions__btn"
            onClick={() => void exportDatev()}
            disabled={!canExport || blocked || stackRows.length === 0}
            title={blocked ? 'Der Export ist gesperrt, solange Belege fehlerhaft sind.' : undefined}
          >
            <FileDown size={15} /> CSV
          </button>
          <button
            type="button"
            className="finance-actions__btn is-pay"
            onClick={() => void exportExtf()}
            disabled={!canExport || blocked || !datevReady || stackRows.length === 0}
            title={!datevReady ? 'Berater- und Mandantennummer fehlen – siehe „Übergaben & Nummernkreis".' : undefined}
          >
            <FileDown size={15} /> DATEV EXTF
          </button>
        </>
      )}
      metrics={(
        <CustomizableMetrics
          pageKey="finance-buchhaltung"
          tiles={[
            { key: 'vat', node: <MetricTile label="Umsatzsteuer" value={money(vat.vatTotal)} hint={`${vat.outgoingCount} Ausgangsbelege`} tone="info" /> },
            { key: 'inputtax', node: <MetricTile label="Vorsteuer" value={money(vat.inputTaxTotal)} hint={`${vat.incomingCount} Eingangsbelege`} tone="default" /> },
            { key: 'payable', node: (
              <MetricTile
                label={refund ? 'Erstattung' : 'Zahllast'}
                value={money(Math.abs(vat.payable))}
                hint={periodLabel(period)}
                tone={refund ? 'default' : 'warning'}
              />
            ) },
            { key: 'entries', node: <MetricTile label="Buchungssätze" value={stackRows.length} hint="im Stapel" tone="default" /> },
          ]}
        />
      )}
      toolbar={(
        <div className="finance-toolbar">
          <FinanceNav />
          <PeriodFilter period={period} onChange={setPeriod} />
          <Tabs items={viewTabs} activeId={view} onSelect={(id) => setView(id as BookkeepingView)} ariaLabel="Buchhaltungsbereich" />
        </div>
      )}
    >
      {error && <div className="finance-page__error">{error}</div>}

      <p className="finance-note is-warning">
        <Info size={15} aria-hidden />
        <span>
          <strong>Vorbereitend, nicht rechtsverbindlich:</strong> Diese Übersicht wird aus den gebuchten FORQ-Belegen
          abgeleitet, damit die Voranmeldung geprüft und übergeben werden kann. Gebucht und gemeldet wird in der
          Fibu bzw. über ELSTER.
        </span>
      </p>

      {!loading && repeat === 'changed' && (
        <p className="finance-note is-warning">
          <TriangleAlert size={15} aria-hidden />
          <span>
            <strong>Dieser Zeitraum wurde schon einmal übergeben – und hat sich seitdem geändert.</strong> Wenn
            der Steuerberater die alte Datei bereits gebucht hat, würde ein erneuter Import doppelt buchen.
            Vorher klären, was mit der ersten Übergabe passiert ist (siehe „Übergaben & Nummernkreis").
          </span>
        </p>
      )}

      {loading ? (
        <p className="finance-work__empty">Buchhaltungsdaten werden geladen …</p>
      ) : view === 'log' ? (
        <>
          {/* Beide Blöcke sind ANZEIGE, nicht Eingabe. Bis 09/2026 hatten sie eigene Eingabefelder,
              die in den localStorage des jeweiligen Browsers schrieben -- ein Kollege am zweiten
              Rechner exportierte damit mit anderen (oder gar keinen) Angaben. Geändert wird jetzt
              nur noch in der Grundkonfiguration, die je Mandant in der Datenbank liegt. */}
          <section className="finance-report">
            <h3 className="finance-section__title">DATEV-Mandat <small>· aus der Grundkonfiguration</small></h3>
            <p className="finance-report__lead">
              Berater- und Mandantennummer stehen im Kopf jeder EXTF-Datei – ohne sie weist DATEV den Import ab.
              Beide bekommt ihr von eurem Steuerberater; geraten wird hier nichts.
            </p>
            {!datevReady && (
              <ul className="finance-check-panel__inline">
                {datevSettingsIssues(settings).map((issue) => <li key={issue}>{issue}</li>)}
              </ul>
            )}
            <dl className="finance-settings-readout">
              <div><dt>Beraternummer</dt><dd>{settings.consultantNumber || '—'}</dd></div>
              <div><dt>Mandantennummer</dt><dd>{settings.clientNumber || '—'}</dd></div>
              <div><dt>Beginn Wirtschaftsjahr</dt><dd>{settings.fiscalYearStart ? dateLabel(settings.fiscalYearStart) : '—'}</dd></div>
              <div><dt>Länge der Sachkonten</dt><dd>{settings.accountLength}</dd></div>
            </dl>
            <p className="finance-source">
              {grundkonfigurationHinweis} Eure Sachkonten sind vierstellig, Personenkonten damit fünfstellig –
              Debitoren 10000–69999, Kreditoren 70000–99999. Kunden- und Lieferantennummern sind die Personenkonten.
            </p>
          </section>

          <section className="finance-report">
            <h3 className="finance-section__title">Firmenstammdaten <small>· für die E-Rechnung, aus der Grundkonfiguration</small></h3>
            <p className="finance-report__lead">
              Eine XRechnung trägt eure Identität, Anschrift, USt-IdNr., einen Ansprechpartner und das
              Zahlungskonto – EN 16931 macht alles davon zur Pflicht. Ohne diese Angaben verweigert FORQ die
              E-Rechnung, statt ein unvollständiges Pflichtdokument auszugeben.
            </p>
            {!sellerReady && (
              <ul className="finance-check-panel__inline">
                {sellerSettingsIssues(seller).map((issue) => <li key={issue}>{issue}</li>)}
              </ul>
            )}
            <dl className="finance-settings-readout">
              <div><dt>Firmenname</dt><dd>{seller.name || '—'}</dd></div>
              <div><dt>Anschrift</dt><dd>{[seller.street, [seller.postalCode, seller.city].filter(Boolean).join(' '), seller.countryCode].filter(Boolean).join(', ') || '—'}</dd></div>
              <div><dt>USt-IdNr.</dt><dd>{seller.vatId || '—'}</dd></div>
              <div><dt>Steuernummer</dt><dd>{seller.taxNumber || '—'}</dd></div>
              <div><dt>Ansprechpartner</dt><dd>{seller.contactName || '—'}</dd></div>
              <div><dt>Telefon</dt><dd>{seller.contactPhone || '—'}</dd></div>
              <div><dt>E-Mail</dt><dd>{seller.contactEmail || '—'}</dd></div>
              <div><dt>IBAN</dt><dd>{seller.iban || '—'}</dd></div>
              <div><dt>BIC</dt><dd>{seller.bic || '—'}</dd></div>
              <div><dt>Bank</dt><dd>{seller.bankName || '—'}</dd></div>
            </dl>
            <p className="finance-source">
              {grundkonfigurationHinweis} Die IBAN wird auf ihre Prüfziffer geprüft – ein Zahlendreher fällt
              hier auf, nicht beim Kunden.
            </p>
          </section>

          <section className={`finance-check-panel${gapsFound ? ' is-warned' : ' is-clean'}`}>
            <header>
              {gapsFound ? <TriangleAlert size={16} aria-hidden /> : <ShieldCheck size={16} aria-hidden />}
              <div>
                <strong>{gapsFound ? 'Lücken im Rechnungsnummernkreis' : 'Rechnungsnummern lückenlos'}</strong>
                <small>
                  Die GoBD verlangt eine lückenlose Nummernfolge. Stornierte Rechnungen behalten ihre Nummer und
                  zählen deshalb als vorhanden – eine echte Lücke heißt: gelöscht oder nie übergeben.
                </small>
              </div>
            </header>
            <ul>
              {numberRanges.length === 0 ? (
                <li className="is-warning"><Info size={13} aria-hidden /><span>Noch keine Rechnungsnummern vergeben.</span></li>
              ) : numberRanges.map((range) => (
                <li key={range.prefix} className={range.gaps.length > 0 ? 'is-warning' : 'is-ok'}>
                  {range.gaps.length > 0 ? <TriangleAlert size={13} aria-hidden /> : <ShieldCheck size={13} aria-hidden />}
                  <span>
                    <strong>{range.prefix}</strong> · {range.present} Nummern von {range.prefix}-{String(range.lowest).padStart(6, '0')} bis {range.prefix}-{String(range.highest).padStart(6, '0')}
                    {range.gaps.length > 0
                      ? ` — fehlt: ${range.gaps.map(formatGap).join(', ')}`
                      : ' — keine Lücke'}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="finance-report">
            <h3 className="finance-section__title">Übergaben an den Steuerberater</h3>
            <p className="finance-report__lead">
              Jeder Export wird protokolliert – mit Zeitraum, Summen und einer Prüfsumme des Inhalts. Nur so
              lässt sich später sagen, ob ein Monat schon übergeben wurde und ob sich seitdem etwas geändert hat.
            </p>
            {exports.length === 0 ? (
              <p className="finance-work__empty">Noch nichts exportiert.</p>
            ) : (
              <div className="finance-stack-wrap">
                <table className="finance-stack">
                  <thead>
                    <tr>
                      <th scope="col">Exportiert am</th><th scope="col">Von</th><th scope="col">Zeitraum</th>
                      <th scope="col">Format</th><th scope="col">Sätze</th><th scope="col">Debitoren</th>
                      <th scope="col">Kreditoren</th><th scope="col">Prüfsumme</th><th scope="col">Datei</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exports.map((entry) => (
                      <tr key={entry.id}>
                        <td>{dateLabel(entry.exportedAt.slice(0, 10))}</td>
                        <td>{entry.exportedBy}</td>
                        <td>{entry.from && entry.to ? `${dateLabel(entry.from)} – ${dateLabel(entry.to)}` : 'Gesamt'}</td>
                        <td>{exportFormatLabels[entry.format]}</td>
                        <td className="forq-cell-num">{entry.rowCount}</td>
                        <td className="forq-cell-num">{money(entry.debitorTotal)}</td>
                        <td className="forq-cell-num">{money(entry.kreditorTotal)}</td>
                        <td><code>{entry.checksum}</code></td>
                        <td>{entry.fileName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : view === 'vat' ? (
        <>
          <div className="finance-report-grid">
            <section className="finance-report">
              <h3 className="finance-section__title">Umsatzsteuer <small>· Ausgangsrechnungen</small></h3>
              <table className="finance-statement">
                <thead>
                  <tr><th scope="col">Steuersatz</th><th scope="col">Netto</th><th scope="col">Umsatzsteuer</th></tr>
                </thead>
                <tbody>
                  {vat.revenueByRate.length === 0 ? (
                    <tr><td colSpan={3}>Keine Ausgangsbelege im Zeitraum.</td></tr>
                  ) : vat.revenueByRate.map((row) => (
                    <tr key={row.rate}>
                      <th scope="row">{row.rate} %</th>
                      <td className="forq-cell-num">{money(row.net)}</td>
                      <td className="forq-cell-num">{money(row.tax)}</td>
                    </tr>
                  ))}
                  <tr className="is-sum">
                    <th scope="row">Summe</th>
                    <td className="forq-cell-num">{money(vat.revenueByRate.reduce((sum, row) => sum + row.net, 0))}</td>
                    <td className="forq-cell-num">{money(vat.vatTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section className="finance-report">
              <h3 className="finance-section__title">Vorsteuer <small>· Eingangsrechnungen</small></h3>
              <table className="finance-statement">
                <thead>
                  <tr><th scope="col">Steuersatz</th><th scope="col">Netto</th><th scope="col">Vorsteuer</th></tr>
                </thead>
                <tbody>
                  {vat.inputTaxByRate.length === 0 ? (
                    <tr><td colSpan={3}>Keine kontierten Eingangsbelege im Zeitraum.</td></tr>
                  ) : vat.inputTaxByRate.map((row) => (
                    <tr key={row.rate}>
                      <th scope="row">{row.rate} %</th>
                      <td className="forq-cell-num">{money(row.net)}</td>
                      <td className="forq-cell-num">{money(row.tax)}</td>
                    </tr>
                  ))}
                  <tr className="is-sum">
                    <th scope="row">Summe</th>
                    <td className="forq-cell-num">{money(vat.inputTaxByRate.reduce((sum, row) => sum + row.net, 0))}</td>
                    <td className="forq-cell-num">{money(vat.inputTaxTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </div>

          <section className="finance-report">
            <div className="finance-section__head">
              <h3 className="finance-section__title">Vorbereitete Umsatzsteuer-Voranmeldung <small>· {periodLabel(period)}</small></h3>
              <button
                type="button"
                className="finance-actions__btn"
                onClick={() => downloadCsv(toVatCsv(vat), `FORQ_USt-VA_${tenant.shortName}_${periodLabel(period).replace(/\s+/g, '-')}.csv`)}
                disabled={!canExport}
              >
                <FileDown size={14} /> USt-VA als CSV
              </button>
            </div>
            <table className="finance-statement">
              <tbody>
                <tr><th scope="row">Umsatzsteuer aus Lieferungen und Leistungen</th><td className="forq-cell-num">{money(vat.vatTotal)}</td></tr>
                <tr><th scope="row">abziehbare Vorsteuerbeträge</th><td className="forq-cell-num">{money(-vat.inputTaxTotal)}</td></tr>
                <tr className="is-sum">
                  <th scope="row">{refund ? 'Erstattungsanspruch' : 'Verbleibende Umsatzsteuer-Vorauszahlung'}</th>
                  <td className="forq-cell-num">{money(Math.abs(vat.payable))}</td>
                </tr>
              </tbody>
            </table>
            <p className="finance-source">
              Quelle: {vat.outgoingCount} Ausgangsbelege mit Rechnungsnummer, {vat.incomingCount} kontierte
              Eingangsrechnungen · Datenstand {dateLabel(today)} · Gutschriften sind gegengerechnet ·
              Übermittlung an ELSTER erfolgt außerhalb von FORQ.
            </p>
          </section>
        </>
      ) : (
        <>
          <section className={`finance-check-panel${blocked ? ' is-blocked' : issues.length > 0 ? ' is-warned' : ' is-clean'}`}>
            <header>
              {blocked ? <ShieldAlert size={16} aria-hidden /> : <ShieldCheck size={16} aria-hidden />}
              <div>
                <strong>
                  {blocked
                    ? `Export gesperrt – ${errors.length} ${errors.length === 1 ? 'Fehler' : 'Fehler'} im Zeitraum`
                    : issues.length > 0
                      ? `Export möglich – ${issues.length} ${issues.length === 1 ? 'Hinweis' : 'Hinweise'}`
                      : 'Alle Belege geprüft – Export freigegeben'}
                </strong>
                <small>
                  {blocked
                    ? 'Der Steuerberater bucht aus dieser Datei. Fehlerhafte Belege werden nicht exportiert, sondern erst hier korrigiert.'
                    : 'Kontenplan, Steuersätze, Nummernkreise und Summen wurden gegen die Belege geprüft.'}
                </small>
              </div>
            </header>
            {issues.length > 0 && (
              <ul>
                {issues.map((issue, index) => (
                  <li key={`${issue.documentNumber}-${index}`} className={`is-${issue.severity}`}>
                    {issue.severity === 'error' ? <CircleAlert size={13} aria-hidden /> : <Info size={13} aria-hidden />}
                    <span><strong>{issue.documentNumber}</strong> · {issue.party} — {issue.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="finance-report">
            <h3 className="finance-section__title">Kontrollblatt <small>· {periodLabel(period)}</small></h3>
            <p className="finance-report__lead">
              Die Summe der Buchungssätze muss die Summe der Belege ergeben – nur dieser Abgleich beweist, dass
              nichts verloren gegangen oder doppelt drin ist.
            </p>
            <table className="finance-statement">
              <thead>
                <tr><th scope="col">Abgleich</th><th scope="col">Buchungssätze</th><th scope="col">Belege</th><th scope="col">Differenz</th></tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Debitoren (Gutschriften abgezogen)</th>
                  <td className="forq-cell-num">{money(reconciliation.debitorTotal)}</td>
                  <td className="forq-cell-num">{money(reconciliation.invoiceTotal)}</td>
                  <td className={`forq-cell-num${reconciliation.debitorDifference !== 0 ? ' finance-overdue' : ''}`}>{money(reconciliation.debitorDifference)}</td>
                </tr>
                <tr>
                  <th scope="row">Kreditoren</th>
                  <td className="forq-cell-num">{money(reconciliation.kreditorTotal)}</td>
                  <td className="forq-cell-num">{money(reconciliation.incomingTotal)}</td>
                  <td className={`forq-cell-num${reconciliation.kreditorDifference !== 0 ? ' finance-overdue' : ''}`}>{money(reconciliation.kreditorDifference)}</td>
                </tr>
                <tr className="is-sum">
                  <th scope="row">{reconciliation.ok ? 'Stapel stimmt mit den Belegen überein' : 'Stapel weicht von den Belegen ab'}</th>
                  <td className="forq-cell-num">{reconciliation.rowCount} Sätze</td>
                  <td className="forq-cell-num" colSpan={2}>
                    <StatusPill label={reconciliation.ok ? 'Geprüft' : 'Abweichung'} tone={reconciliation.ok ? 'done' : 'danger'} />
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="finance-report">
            <h3 className="finance-section__title">Buchungsstapel</h3>
            <p className="finance-report__lead">
              Ein Buchungssatz je Erlöskonto und Steuersatz. Der Umsatz ist immer positiv – die Richtung steht im
              Soll/Haben-Kennzeichen, so wie DATEV es erwartet.
            </p>
            <div className="finance-stack-wrap">
              <table className="finance-stack">
                <thead>
                  <tr>
                    <th scope="col">Belegdatum</th><th scope="col">Belegfeld 1</th><th scope="col">Umsatz</th>
                    <th scope="col">S/H</th><th scope="col">Konto</th><th scope="col">Gegenkonto</th>
                    <th scope="col">Steuer</th><th scope="col">Buchungstext</th><th scope="col">KOST1</th>
                  </tr>
                </thead>
                <tbody>
                  {stackRows.length === 0 ? (
                    <tr><td colSpan={9}>Keine buchbaren Belege im Zeitraum.</td></tr>
                  ) : stackRows.map((row, index) => (
                    <tr key={`${row.richtung}-${row.belegfeld1}-${row.gegenkonto}-${index}`}>
                      <td>{dateLabel(row.belegdatum)}</td>
                      <td>{row.belegfeld1}</td>
                      <td className="forq-cell-num">{money(row.umsatz)}</td>
                      <td><span className={`finance-sh is-${row.sollHaben.toLowerCase()}`}>{row.sollHaben}</span></td>
                      <td title={accountLabel(row.konto)}>{row.konto}</td>
                      <td title={accountLabel(row.gegenkonto)}>{accountLabel(row.gegenkonto)}</td>
                      <td>{taxCodeDefinition(row.taxCode).label}</td>
                      <td>{row.buchungstext}</td>
                      <td>{row.kost1 || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </WorkspacePage>
  );
}
